import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useClasseContext } from '../context/ClasseContext';
import { useUser } from '../context/UserContext';

const ROUTE_LABELS = {
    'home': 'Accueil',
    'account': 'Mon compte',
    'passation': 'Passation élève',
    'detail': 'Tableau de bord',
    'progression': 'Programmation',
    'evals': 'Évaluations',
    'eleves': 'Élèves',
    'savoirs': 'Savoirs',
    'eval-nat': 'Évaluation nationale',
    'eval-bilan': 'QCM de suivi',
    'qcm': 'Banque TanoEd',
    'creer': 'Créer une évaluation',
    'synchronisation': 'Synchronisation GAR',
    'common-eval': 'Devoir commun',
    'add-common-eval': 'Devoir commun',
    'admin-groupe': 'Configuration des groupes',
    'groupes-common': 'Répartition par résultats',
    'eval-nat-niveau': 'Résultats nationaux',
    'professeur_gestion': 'Gestion des professeurs',
    'periode_scolaire': 'Périodes scolaires',
    'ecoles': 'École de rattachement',
    'evolution': 'Évolution des résultats',
    'choix-etablissement': 'Choix de l\'établissement',
    'mentions': 'Mentions légales',
    'rgpd': 'RGPD',
    'cgu': 'CGU',
    'session': 'Session',
    'exerciseur': 'Exerciseur',
};

// Routes du flux de création : le fil d'Ariane y insère un segment
// "Créer une évaluation" cliquable qui ramène au hub creer/:idclasse.
const CREATION_ROUTES = new Set(['creer', 'qcm', 'eval-bilan']);

export const useBreadcrumb = () => {
    const location = useLocation();
    const { classeNom, groupNom } = useClasseContext();
    const { etablissementData } = useUser();

    return useMemo(() => {
        const segments = location.pathname.split('/').filter(Boolean);
        const out = [{ label: 'Accueil', href: '/home/' }];

        // On saute le préfixe "home"
        const meaningful = segments[0] === 'home' ? segments.slice(1) : segments;
        if (meaningful.length === 0) return out;

        const route = meaningful[0];
        const idFromUrl = meaningful.find(s => /^\d+$/.test(s));
        // add-common-eval ne porte pas d'id en URL : on retrouve la classe/le groupe
        // ouvert via l'établissement pour pouvoir lier le segment "Créer une évaluation".
        const idFromContext = classeNom
            ? etablissementData?.classes?.find(c => c.nom === classeNom)?.id
            : groupNom
                ? etablissementData?.groupes?.find(g => g.nom === groupNom)?.id
                : null;
        const idClasse = idFromUrl || idFromContext;
        const hrefHub = idClasse ? `/home/creer/${idClasse}` : undefined;

        const contexteLabel = classeNom ? `Classe ${classeNom}` : groupNom ? `Groupe ${groupNom}` : null;

        const isSession = route === 'qcm' && meaningful.includes('session');
        const isCreationFlow = !isSession && (
            CREATION_ROUTES.has(route) ||
            route === 'add-common-eval' ||
            (route === 'evals' && meaningful[2] === 'add')
        );

        // Segment classe/groupe en premier (modèle mental : Accueil / Classe / Page).
        // Cliquable vers l'atterrissage de la classe (le hub Créer), sauf dans le flux
        // de création où c'est le segment "Créer une évaluation" qui porte le lien.
        if (contexteLabel && idFromUrl) {
            out.push({
                label: contexteLabel,
                href: (!isCreationFlow && !isSession) ? hrefHub : undefined,
            });
        }

        // Zone stable ArUco : qcm/:id/session/:sid/... → retour vers la banque.
        if (isSession) {
            out.push({ label: ROUTE_LABELS['qcm'], href: idFromUrl ? `/home/qcm/${idFromUrl}` : undefined });
            out.push({ label: ROUTE_LABELS['session'] });
            return out;
        }

        if (isCreationFlow) {
            if (route === 'creer') {
                out.push({ label: ROUTE_LABELS['creer'] });
                return out;
            }
            out.push({ label: ROUTE_LABELS['creer'], href: hrefHub });
            out.push({ label: route === 'evals' ? "J'ai déjà mon sujet" : (ROUTE_LABELS[route] || route) });
            return out;
        }

        // Cas général : labels mappés, les ids ont déjà été traités ci-dessus.
        for (const seg of meaningful) {
            if (/^\d+$/.test(seg)) continue;
            out.push({ label: ROUTE_LABELS[seg] || seg });
        }
        return out;
    }, [location.pathname, classeNom, groupNom, etablissementData]);
};
